import * as React from 'react';
import {
    List,
    DataTable,
    NumberField,
    FunctionField,
    SelectInput,
    NumberInput,
    TopToolbar,
    ExportButton,
    ColumnsButton,
    FilterButton,
    useRecordContext,
} from 'react-admin';
import { Chip, Box, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAccessToken } from '../authProvider';
import { API_BASE_URL } from '../config';
import { AI_STATUS_CHOICES, AI_STATUS_MAP } from './types';
import AiVideoStats from './AiVideoStats';

const aiVideoFilters = [
    <SelectInput 
        key="aiStatus" 
        label="AI Status" 
        source="aiStatus" 
        choices={AI_STATUS_CHOICES}         
    />,
    <NumberInput 
        key="aiTimeMin" 
        label="Min AI Time (seconds)" 
        source="aiTimeMin" 
    />,
    <NumberInput 
        key="aiTimeMax" 
        label="Max AI Time (seconds)" 
        source="aiTimeMax" 
    />,
];

const AiVideoListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ColumnsButton />
        <ExportButton />
    </TopToolbar>
);

const StatusField = () => {
    const record = useRecordContext();
    if (!record) return null;

    const getStatusColor = (status: number): 'default' | 'primary' | 'error' | 'success' => {
        switch (status) {
            case 0: return 'default';
            case 1: return 'primary';
            case 2: return 'error';
            case 9: return 'success';
            default: return 'default';
        }
    };

    return (
        <Chip
            label={AI_STATUS_MAP[record.aiStatus] || `Status ${record.aiStatus}`}
            color={getStatusColor(record.aiStatus)}
            size="small"
        />
    );
};

const VideoPicField = () => {
    const record = useRecordContext();
    const [token, setToken] = React.useState<string>('');
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const fetchToken = async () => {
            try {
                const accessToken = await getAccessToken();
                if (accessToken) {
                    setToken(accessToken);
                }
            } catch (error) {
                console.error('Failed to get access token:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchToken();
    }, []);

    if (!record) return null;

    // Show loading state while token is being fetched
    if (loading) {
        return (
            <Box
                sx={{
                    width: 80,
                    height: 60,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                }}
            >
                Loading...
            </Box>
        );
    }

    // Only generate URLs if token is available
    const picUrl = token && record.pic
        ? `${API_BASE_URL}/r2/download${record.pic}?authorization=${token}`
        : undefined;

    const videoUrl = token && record.url
        ? `${API_BASE_URL}/r2/download${record.url}?authorization=${token}`
        : undefined;

    const handleClick = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <Box
                onClick={handleClick}
                sx={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    '&:hover': {
                        opacity: 0.8,
                    },
                }}
            >
                {picUrl ? (
                    <img
                        src={picUrl}
                        alt="Video thumbnail"
                        style={{
                            width: 80,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 4,
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: 80,
                            height: 60,
                            backgroundColor: '#f0f0f0',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        No Image
                    </Box>
                )}
            </Box>
            
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
            >
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'white',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        zIndex: 1,
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ p: 0, backgroundColor: 'black' }}>
                    {videoUrl && (
                        <video
                            controls
                            autoPlay
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: '80vh',
                            }}
                        >
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

const TimeField = ({ source }: { source: string }) => {
    const record = useRecordContext();
    if (!record) return null;

    const timestamp = record[source];
    if (!timestamp) return null;

    // Convert Unix timestamp (seconds) to Date
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

    return <span>{formatted}</span>;
};

type AiVideo = {
    id: number;
    url: string;
    pic: string;
    aiStatus: number;
    aiStartTime: number;
    aiEndTime: number;
    aiTime: number;
};

const Column = DataTable.Col<AiVideo>;

const AiVideoList = () => {
    return (
        <>
            <AiVideoStats />
            <List
                filters={aiVideoFilters}
                actions={<AiVideoListActions />}
                perPage={10}
                sort={{ field: 'id', order: 'ASC' }}
            >
                <DataTable bulkActionButtons={false}>
                    <Column source="id" label="ID">
                        <NumberField source="id" />
                    </Column>
                    <Column source="pic" label="Video">
                        <VideoPicField />
                    </Column>
                    <Column source="aiStatus" label="Status">
                        <StatusField />
                    </Column>
                    <Column source="aiStartTime" label="Start Time">
                        <TimeField source="aiStartTime" />
                    </Column>
                    <Column source="aiEndTime" label="End Time">
                        <TimeField source="aiEndTime" />
                    </Column>
                    <Column source="aiTime" label="AI Time (seconds)">
                        <NumberField source="aiTime" />
                    </Column>
                </DataTable>
            </List>
        </>
    );
};

export default AiVideoList;
